///
/// EarthBrowser
///
/// Created by Matt Giger on 11/29/2025
/// Copyright (c) 2025 Geodata Labs
///

import { EarthTileIndexBuffer } from "@/earthbrowser/activities/EarthActivity/tilecache/EarthTileGeometry"
import { TerrainNormalBaker } from "@/earthbrowser/activities/EarthActivity/tilecache/TerrainNormalBaker"
import { FramePairPacker } from "@/earthbrowser/datasets/FramePairPacker"
import { RadarVolumeMask } from "@/earthbrowser/datasets/RadarVolumeMask"
import { SelectionCoordinatorClass } from "@/earthbrowser/design-system/SelectionCoordinator"
import type { FocusedObjectCallout } from "@/earthbrowser/design-system/FocusedObjectCallout"
import { HexSphereGeometry } from "@/earthbrowser/entities/geometries/HexSphereGeometry"
import { type GLTFGeometry } from "@/earthbrowser/entities/GLTF/GLTFGeometry"
import { type GLTFMaterial } from "@/earthbrowser/entities/GLTF/GLTFMaterial"
import { GLTFShaderProgram } from "@/earthbrowser/entities/GLTF/GLTFShaderProgram"
import { ComponentType, InternalFormat, PixelFormat, TextureFilter, TextureWrap } from "@stovokor/shader/GLConstants"
import { combineDefines } from "@stovokor/shader/ShaderSourceCode"
import { Texture } from "@stovokor/shader/Texture"
import { StovokorContext } from "@stovokor/StovokorContext"
import { simpleHash } from "@stovokor/utils/hashString"
import { BaseElevationLayer } from "@/earthbrowser/activities/EarthActivity/utils/BaseElevationLayer"
import { type geographic } from "@/earthbrowser/utils/geographic"
import { ImageLoader } from "@/earthbrowser/utils/loaders/ImageLoader"

// Bump the version when tools/water_mask regenerates the mask
const cWaterMaskUrl = "/images/shaders/watermask.png?v=2"

/**
 * EarthBrowser specific context for the engine.
 */
export class EarthBrowserContext extends StovokorContext {
	public readonly clock = new Clock()
	public readonly blankElevation: Texture
	/** Flat 1x1 normal map for tiles without their own baked terrain normals */
	public readonly blankNormal: Texture
	public readonly terrainNormals: TerrainNormalBaker
	/** Packs a texture series' frame pair into one RG texture (see TextureSeries.packPairs) */
	public readonly framePairs: FramePairPacker
	/** Footprints of the NEXRAD volumes drawing this frame, for the flat radar renderers to step back under */
	public readonly radarVolumeMask = new RadarVolumeMask()
	public readonly hexSphereGeometry: HexSphereGeometry
	public readonly indexBufferCache = new Map<string, EarthTileIndexBuffer>()
	public readonly gltfShaderCache = new Map<string, GLTFShaderProgram>()
	public readonly selectionCoordinator = new SelectionCoordinatorClass(this)
	public focusedObjectCallout?: FocusedObjectCallout
	public hoveredObjectCallout?: FocusedObjectCallout
	public readonly baseElevation: BaseElevationLayer

	/**
	 * Global water mask for the earth tile shader's sun glint: R8
	 * equirectangular, 255 ocean / 128 land / 0 lake, built from Natural
	 * Earth by tools/water_mask. Streams in after startup; the tile
	 * renderer passes hasWaterMask until it is available
	 */
	public readonly waterMask: Texture

	/**
	 * Vertical exaggeration of displaced terrain. The earth tile and polyline
	 * shaders read it every frame and the relief normals/occlusion are baked
	 * with it. Fixed: the user controls for it were removed.
	 */
	public readonly terrainExaggeration = 1.2

	/** Swing the relief light toward the real sun around sunrise/sunset. Fixed on */
	public readonly terrainSunLight = true

	/**
	 * Bumped whenever terrainExaggeration changes, so nodes that seat
	 * themselves on the terrain with displayElevationKm can notice and
	 * re-apply their placement. Never changes while the exaggeration is fixed
	 */
	public readonly terrainVersion = 0

	/**
	 * Terrain height (km) the globe actually renders at a position: the base
	 * elevation grid scaled by the current exaggeration, zero over the ocean.
	 */
	public displayElevationKm(geo: geographic): number {
		const meters = this.baseElevation.getElevation(geo)
		return meters > 0 ? meters / 1000 * this.terrainExaggeration : 0
	}

	/**
	 * Restricted interaction mode (embeds): user gestures may only focus
	 * entity nodes allowed by entityFocusFilter, and authoring gestures
	 * (drop-pin menu, story drawing) are not installed. The embed entry
	 * sets this before the engine activities are constructed.
	 */
	public restrictedInteraction = false

	/** In restricted mode, limits which nodes user gestures may focus */
	public entityFocusFilter?: (node: unknown) => boolean

	/** Whether a user gesture may focus the given entity node */
	public canFocusEntity(node: unknown): boolean {
		if (!this.restrictedInteraction) return true
		return this.entityFocusFilter?.(node) ?? false
	}

	constructor(canvas: HTMLCanvasElement) {
		super(canvas)

		// packed-byte elevation format (see ElevationLoader), NEAREST like the
		// per-tile textures so the blank stays sample-compatible
		this.blankElevation = new Texture(this, 1, 1,
			InternalFormat.RGB8,
			PixelFormat.RGB,
			ComponentType.UnsignedByte,
			TextureFilter.Nearest,
			TextureFilter.Nearest)

		this.terrainNormals = new TerrainNormalBaker(this)
		this.blankNormal = this.terrainNormals.createTarget()
		this.framePairs = new FramePairPacker(this)

		this.hexSphereGeometry = new HexSphereGeometry(this, true)
		this.baseElevation = new BaseElevationLayer()
		void this.baseElevation.load()

		// Wraps across the antimeridian; the grayscale upload keeps the
		// LINEAR filter and adds mipmaps for the global views
		this.waterMask = new Texture(this, 1, 1,
			InternalFormat.R8,
			PixelFormat.Red,
			ComponentType.UnsignedByte,
			TextureFilter.Linear,
			TextureFilter.Linear,
			TextureWrap.Repeat,
			TextureWrap.ClampToEdge)
		new ImageLoader(cWaterMaskUrl).loadGrayscaleIntoTexture(this.waterMask, true).catch(() => undefined)
	}

	override destroy(): void {
		this.blankElevation.destroy()
		this.blankNormal.destroy()
		this.terrainNormals.destroy()
		this.framePairs.destroy()
		this.hexSphereGeometry.destroy()
		this.indexBufferCache.forEach((value, _key) => {
			value.unpurgable = false // Allow destruction
			value.destroy()
		})
		this.gltfShaderCache.forEach((shader) => shader.destroy())
		this.baseElevation.destroy()
		this.waterMask.destroy()

		super.destroy()
	}

	public indexBuffer(sections: number, resolution: number, skirt: boolean = false): EarthTileIndexBuffer {
		const key = `${resolution}x${sections}${skirt ? 's' : ''}`
		let indexBuffer = this.indexBufferCache.get(key)
		if (indexBuffer === undefined) {
			indexBuffer = new EarthTileIndexBuffer(this, sections, resolution, skirt)
			this.indexBufferCache.set(key, indexBuffer)
		}
		return indexBuffer
	}

	public getGltfShaderProgram(geometry: GLTFGeometry, material: GLTFMaterial): GLTFShaderProgram {
		const defines = combineDefines([geometry.requiredAttributes(), material.requiredAttributes()])
		const key = String(simpleHash(JSON.stringify(defines)))

		let shaderProgram = this.gltfShaderCache.get(key)
		if (!shaderProgram) {
			shaderProgram = new GLTFShaderProgram(this, geometry, material)
			this.gltfShaderCache.set(key, shaderProgram)
		}
		return shaderProgram
	}

	public getShaderSourceString(sourceId: string): string {
		const sourceCode = localShaderSourceMap[sourceId]
		if (!sourceCode) {
			this.logger.error(`Shader not found: ${sourceId}`)
			return ''
		}
		return sourceCode
	}
}

/**
 * Map of shader paths to shader content
 */
import aurora_field_glsl from './shaders/aurora_field.glsl?raw'
import aurora_volume_glsl from './shaders/aurora_volume.glsl?raw'
import buoy_marker_glsl from './shaders/buoy_marker.glsl?raw'
import cloud_field_glsl from './shaders/cloud_field.glsl?raw'
import color_space_glsl from './shaders/color_space.glsl?raw'
import cutout_common_glsl from './shaders/cutout_common.glsl?raw'
import cutout_hypocenter_glsl from './shaders/cutout_hypocenter.glsl?raw'
import cutout_plane_glsl from './shaders/cutout_plane.glsl?raw'
import cutout_slab_glsl from './shaders/cutout_slab.glsl?raw'
import cutout_terrain_glsl from './shaders/cutout_terrain.glsl?raw'
import cutout_wall_glsl from './shaders/cutout_wall.glsl?raw'
import earthtile_glsl from './shaders/earthtile.glsl?raw'
import elevation_sample_glsl from './shaders/elevation_sample.glsl?raw'
import ember_dome_instanced_glsl from './shaders/ember_dome_instanced.glsl?raw'
import frame_pair_glsl from './shaders/frame_pair.glsl?raw'
import quake_shockwave_glsl from './shaders/quake_shockwave.glsl?raw'
import satellite_marker_glsl from './shaders/satellite_marker.glsl?raw'
import volcano_cone_glsl from './shaders/volcano_cone.glsl?raw'
import gltf_glsl from './shaders/gltf.glsl?raw'
import overlay_clouds_glsl from './shaders/overlay_clouds.glsl?raw'
import overlay_ground_glsl from './shaders/overlay_ground.glsl?raw'
import overlay_imerg_glsl from './shaders/overlay_imerg.glsl?raw'
import overlay_snow_glsl from './shaders/overlay_snow.glsl?raw'
import overlay_sst_glsl from './shaders/overlay_sst.glsl?raw'
import overlay_temp_glsl from './shaders/overlay_temp.glsl?raw'
import geopolyline_glsl from './shaders/geopolyline.glsl?raw'
import milkyway_glsl from './shaders/milkyway.glsl?raw'
import moon_glsl from './shaders/moon.glsl?raw'
import polyline_glsl from './shaders/polyline.glsl?raw'
import quad_glsl from './shaders/quad.glsl?raw'
import quad_depth_glsl from './shaders/quad_depth.glsl?raw'
import selection_sphere_glsl from './shaders/selection_sphere.glsl?raw'
import simple_color_glsl from './shaders/simple_color.glsl?raw'
import simple_tex_glsl from './shaders/simple_tex.glsl?raw'
import stars_glsl from './shaders/stars.glsl?raw'
import storm_column_lid_glsl from './shaders/storm_column_lid.glsl?raw'
import storm_column_lightning_glsl from './shaders/storm_column_lightning.glsl?raw'
import storm_column_section_glsl from './shaders/storm_column_section.glsl?raw'
import storm_column_sst_glsl from './shaders/storm_column_sst.glsl?raw'
import storm_column_wall_glsl from './shaders/storm_column_wall.glsl?raw'
import storm_column_wind_glsl from './shaders/storm_column_wind.glsl?raw'
import lightning_glsl from './shaders/lightning.glsl?raw'
import sun_glsl from './shaders/sun.glsl?raw'
import text_lit_glsl from './shaders/text_lit.glsl?raw'
import overlay_radar_glsl from './shaders/overlay_radar.glsl?raw'
import overlay_radar_forecast_glsl from './shaders/overlay_radar_forecast.glsl?raw'
import radar_common_glsl from './shaders/radar_common.glsl?raw'
import volumetric_radar_glsl from './shaders/volumetric_radar.glsl?raw'
import webmercator_tile_glsl from './shaders/webmercator_tile.glsl?raw'
import wireframe_terrain_glsl from './shaders/wireframe_terrain.glsl?raw'
import jetstream_sphere_glsl from './shaders/jetstream_sphere.glsl?raw'
import pressure_isolines_sphere_glsl from './shaders/pressure_isolines_sphere.glsl?raw'
import text_enhanced_glsl from './shaders/text_enhanced.glsl?raw'
import terrain_normal_glsl from './shaders/terrain_normal.glsl?raw'
import vignette_cloud_glsl from './shaders/vignette_cloud.glsl?raw'
import vignette_plinth_glsl from './shaders/vignette_plinth.glsl?raw'
import vignette_precip_glsl from './shaders/vignette_precip.glsl?raw'
import webcam_chip_glsl from './shaders/webcam_chip.glsl?raw'
import webcam_cluster_badge_glsl from './shaders/webcam_cluster_badge.glsl?raw'
import webcam_glow_glsl from './shaders/webcam_glow.glsl?raw'
import { Clock } from "./Clock"

const localShaderSourceMap: Record<string, string> = {
	'aurora_field': aurora_field_glsl,
	'aurora_volume': aurora_volume_glsl,
	'buoy_marker': buoy_marker_glsl,
	'cloud_field': cloud_field_glsl,
	'color_space': color_space_glsl,
	'cutout_common': cutout_common_glsl,
	'cutout_hypocenter': cutout_hypocenter_glsl,
	'cutout_plane': cutout_plane_glsl,
	'cutout_slab': cutout_slab_glsl,
	'cutout_terrain': cutout_terrain_glsl,
	'cutout_wall': cutout_wall_glsl,
	'earthtile': earthtile_glsl,
	'elevation_sample': elevation_sample_glsl,
	'ember_dome_instanced': ember_dome_instanced_glsl,
	'frame_pair': frame_pair_glsl,
	'quake_shockwave': quake_shockwave_glsl,
	'satellite_marker': satellite_marker_glsl,
	'volcano_cone': volcano_cone_glsl,
	'gltf': gltf_glsl,
	'overlay_clouds': overlay_clouds_glsl,
	'overlay_ground': overlay_ground_glsl,
	'overlay_imerg': overlay_imerg_glsl,
	'overlay_radar': overlay_radar_glsl,
	'overlay_radar_forecast': overlay_radar_forecast_glsl,
	'radar_common': radar_common_glsl,
	'overlay_snow': overlay_snow_glsl,
	'overlay_sst': overlay_sst_glsl,
	'overlay_temp': overlay_temp_glsl,
	'geopolyline': geopolyline_glsl,
	'milkyway': milkyway_glsl,
	'moon': moon_glsl,
	'polyline': polyline_glsl,
	'quad': quad_glsl,
	'quad_depth': quad_depth_glsl,
	'selection_sphere': selection_sphere_glsl,
	'simple_color': simple_color_glsl,
	'simple_tex': simple_tex_glsl,
	'stars': stars_glsl,
	'storm_column_lid': storm_column_lid_glsl,
	'storm_column_lightning': storm_column_lightning_glsl,
	'storm_column_section': storm_column_section_glsl,
	'storm_column_sst': storm_column_sst_glsl,
	'storm_column_wall': storm_column_wall_glsl,
	'storm_column_wind': storm_column_wind_glsl,
	'lightning': lightning_glsl,
	'sun': sun_glsl,
	'terrain_normal': terrain_normal_glsl,
	'text_lit': text_lit_glsl,
	'jetstream_sphere': jetstream_sphere_glsl,
	'pressure_isolines_sphere': pressure_isolines_sphere_glsl,
	'text_enhanced': text_enhanced_glsl,
	'vignette_cloud': vignette_cloud_glsl,
	'vignette_plinth': vignette_plinth_glsl,
	'vignette_precip': vignette_precip_glsl,
	'volumetric_radar': volumetric_radar_glsl,
	'webcam_chip': webcam_chip_glsl,
	'webcam_cluster_badge': webcam_cluster_badge_glsl,
	'webcam_glow': webcam_glow_glsl,
	'webmercator_tile': webmercator_tile_glsl,
	'wireframe_terrain': wireframe_terrain_glsl,
}
